/* eslint-disable react/prop-types */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import ReactHighCharts from 'react-highcharts';
import DateRangePicker from 'react-bootstrap-daterangepicker';
import moment from 'moment-timezone';
import _ from 'lodash';
import Highcharts from 'highcharts';
import { translate } from 'react-i18next';

import { placeholderConfig } from '../Highcharts';

const Plot = ReactHighCharts.withHighcharts(Highcharts);

const getTimeFromSeconds = (seconds, daysText, hrsText, minsText) => {
  let hour = Math.floor(seconds / 3600);
  const days = hour > 23 ? Math.floor(hour / 24) : 0;
  const min = Math.floor((seconds / 3600 - hour) * 60);
  if (days > 0) hour -= days * 24;
  return `${days > 0 ? `${days} ${daysText}` : ''} ${
    hour > 0 ? `${hour} ${hrsText}` : ''
  } ${min} ${minsText}`;
};

@translate(['dashboard'], { wait: true })
class DurationPlot extends Component {
  constructor(props) {
    super(props);

    this.handleEvent = this.handleEvent.bind(this);
  }

  shouldComponentUpdate(props) {
    if (
      this.props.durationReport === props.durationReport &&
      this.props.noRidesEver === props.noRidesEver &&
      this.props.currentLanguage === props.currentLanguage
    ) {
      return false;
    }

    return true;
  }

  getRangeLabel() {
    const [m1, m2] = this.props.range;
    m1.locale(this.props.currentLanguage);
    m2.locale(this.props.currentLanguage);
    return `${m1.format('DD MMMM')} - ${m2.format('DD MMMM YYYY')}`;
  }

  handleEvent(e, picker) {
    this.props.handleEvent(e, picker);
  }

  render() {
    const { t, noRidesEver } = this.props;

    const date = this.props.range[0].format('YYYY,M,D').split(',');
    const start = Date.UTC(date[0], date[1] - 1, date[2]);
    const pointInterval = 86400000;
    const daysCount = this.props.range[1].diff(this.props.range[0], 'days') + 1;
    const journeysExpenses = this.props.fuelConsumptionReport.values
      ? this.props.fuelConsumptionReport.values.map(i => i.toFixed(2))
      : [];
    const journeysCosts = this.props.fuelCostReport.values
      ? this.props.fuelCostReport.values.map(i => Math.round(i))
      : [];
    const journeysDistances = this.props.distanceReport.values
      ? this.props.distanceReport.values.map(
          i => Math.round(i / 1000 * 10) / 10,
        )
      : [];
    const journeysDurations = this.props.durationReport.values
      ? this.props.durationReport.values.map((value, index) => ({
          y: value,
          ind: [
            journeysDistances[index],
            journeysCosts[index],
            journeysExpenses[index],
          ],
          className: value === 0 ? 'disabled' : '',
        }))
      : [];
    let config = {
      chart: { type: 'column' },
      tooltip: {
        formatter() {
          // eslint-disable-line func-names
          let s = `<div class="tooltipDate">${Highcharts.dateFormat(
            '%e %b %Y',
            this.x,
          )}</div>`;
          for (const point of this.points) {
            let hour = Math.floor(this.y / 3600);
            const days = hour > 23 ? Math.floor(hour / 24) : 0;
            const min = Math.floor((this.y / 3600 - hour) * 60);
            if (days > 0) hour -= days * 24;
            let _hour;
            if (days === 0) {
              _hour = `<div class="tooltipWrap active"><span>${hour}</span>${t(
                'dashboard:hours',
              )}<span>${min}</span>${t('dashboard:min')}</div>`;
            } else if (days > 0 && hour > 0) {
              _hour = `<div class="tooltipWrap active"><span>${days}</span>${t(
                'dashboard:days',
              )}<span>${hour}</span>${t(
                'dashboard:hours',
              )}<span>${min}</span>${t('dashboard:min')}</div>`;
            } else {
              _hour = `<div class="tooltipWrap active"><span>${min}</span>${t(
                'dashboard:min',
              )}</div>`;
            }
            s +=
              `${_hour}<div class="tooltipWrap"><span class="titile">${t(
                'dashboard:distanceTab',
              )}</span> <span>${point.point.ind[0].toLocaleString('ru')}${t(
                'dashboard:km',
              )}</span></div>` +
              `<div class="tooltipWrap"><span class="titile">${t(
                'dashboard:consumption',
              )}</span> <span>${_.round(point.point.ind[2], 1).toLocaleString(
                'ru',
              )}${t('dashboard:l')}</span></div>`;
          }
          return s;
        },
      },
      plotOptions: {
        series: {
          pointStart: start,
          pointInterval,
        },
      },
      series: [
        {
          name: t('dashboard:timeTab'),
          color: 'rgba(108,50,215,0.4)',
          states: { hover: { color: 'rgba(108,50,215,1)' } },
          data: journeysDurations,
        },
      ],
    };
    config = noRidesEver ? placeholderConfig(start) : config;
    const hrsText = t('dashboard:hours');
    const daysText = t('dashboard:days');
    const minsText = t('dashboard:min');
    const totalDuration = getTimeFromSeconds(
      journeysDurations.reduce((prev, curr) => prev + curr.y, 0),
      daysText,
      hrsText,
      minsText,
    );
    const averageDurationByDay = getTimeFromSeconds(
      journeysDurations.reduce((prev, curr) => prev + curr.y, 0) / daysCount,
      daysText,
      hrsText,
      minsText,
    );

    return (
      <div id="timeTab" className="tab active">
        <div className="graph-section__status">
          <div className="graph-section__filterWrap">
            <div className="filterBlock">
              <div className="filterBlockDrop">
                <DateRangePicker
                  key={this.props.currentLanguage}
                  onHide={this.handleEvent}
                  startDate={this.props.range[0]}
                  endDate={this.props.range[1]}
                  maxDate={moment()}
                  minDate={moment('01/01/2012', 'DD/MM/YYYY')}
                  alwaysShowCalendars={true} // eslint-disable-line react/jsx-boolean-value
                  showDropdowns={false}
                  showWeekNumbers={false}
                  timePicker={false}
                  ranges={{
                    [t('dashboard:last_7_days')]: [
                      moment().subtract(6, 'days'),
                      moment(),
                    ],
                    [t('dashboard:last_30_days')]: [
                      moment().subtract(29, 'days'),
                      moment(),
                    ],
                    [t('dashboard:last_90_days')]: [
                      moment().subtract(89, 'days'),
                      moment(),
                    ],
                  }}
                  showCustomRangeLabel={false}
                  opens={'left'}
                  format={'DD.MM.YYYY'}
                  locale={{
                    format: 'DD.MM.YYYY',
                    customRangeLabel: 'Custom Range',
                    firstDay: 1,
                  }}
                  dateLimit={{ days: 90 }}
                >
                  <div className="pull-right reportrange">
                    <span>{this.getRangeLabel()}</span> <b className="caret" />
                  </div>
                </DateRangePicker>
              </div>
            </div>
          </div>
          <div className="graph-section__filterInfo">
            <div className="graph-section__filterInfo__col">
              <div className="status">{averageDurationByDay}</div>
              <div className="title">{t('dashboard:average')}</div>
            </div>
            <div className="graph-section__filterInfo__col">
              <div className="status">{totalDuration}</div>
              <div className="title">{t('dashboard:total')}</div>
            </div>
          </div>
        </div>
        <Plot
          config={config}
          domProps={{
            id: 'sectionGraphDrivingTime',
            className: 'graph-section__graph',
          }}
        />
      </div>
    );
  }
}

DurationPlot.PropTypes = {
  fuelConsumptionReport: PropTypes.object,
  fuelCostReport: PropTypes.object,
  durationReport: PropTypes.object,
  distanceReport: PropTypes.object,
  range: PropTypes.arrayOf(PropTypes.instanceOf(moment)),
  handleEvent: PropTypes.func,
};

export default connect(
  state => ({
    noRidesEver: !(
      !state.rides.areJourneysEverLoaded || state.rides.areJourneysEverExist
    ),
    fuelConsumptionReport:
      state.dashboard.filteredReports.fuelConsumptionReport,
    fuelCostReport: state.dashboard.filteredReports.fuelCostReport,
    durationReport: state.dashboard.filteredReports.durationReport,
    distanceReport: state.dashboard.filteredReports.distanceReport,
    currentLanguage: state.langs.currentLanguage,
  }),
  () => ({}),
)(DurationPlot);



// WEBPACK FOOTER //
// ./src/components/Dashboard/Plots/DurationPlot.js